<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import { useLlmCredentialStore } from 'dashboard/store/captain/llmCredentials';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ModelDropdown from './ModelDropdown.vue';
import CredentialDropdown from './CredentialDropdown.vue';
import { providerName, resolveFeatureCredential } from '../llmCredentials';

const props = defineProps({
  featureKey: { type: String, required: true },
  title: { type: String, required: true },
  description: { type: String, required: true },
});

const emit = defineEmits(['change', 'credentialChange']);

const { t } = useI18n();
const { features } = storeToRefs(useCaptainConfigStore());
const { records } = storeToRefs(useLlmCredentialStore());

const feature = computed(() => features.value[props.featureKey]);

// What the calls of this feature go out on right now, by the same rule the server uses.
const resolved = computed(() =>
  resolveFeatureCredential(records.value, feature.value)
);
</script>

<template>
  <div
    class="flex flex-col gap-4 p-4 rounded-xl outline outline-1 -outline-offset-1 bg-n-solid-1 lg:flex-row lg:items-start lg:justify-between"
    :class="feature && !resolved ? 'outline-n-amber-6' : 'outline-n-weak'"
  >
    <div class="flex flex-col flex-1 min-w-0 gap-1">
      <h4 class="m-0 text-sm font-medium text-n-slate-12">{{ title }}</h4>
      <p class="m-0 text-sm text-n-slate-11">{{ description }}</p>
      <p
        v-if="feature && resolved"
        class="flex items-center gap-1.5 m-0 mt-1 text-xs text-n-slate-11"
      >
        <Icon icon="i-lucide-circle-check" class="size-3.5 text-n-teal-11" />
        {{
          t('CAPTAIN_SETTINGS.MODEL_CONFIG.RUNS_ON', { name: resolved.name })
        }}
      </p>
      <p
        v-else-if="feature"
        class="flex items-start gap-1.5 m-0 mt-1 text-xs text-n-amber-11"
      >
        <Icon
          icon="i-lucide-triangle-alert"
          class="flex-shrink-0 size-3.5 mt-px"
        />
        {{
          t('CAPTAIN_SETTINGS.MODEL_CONFIG.NO_LLM', {
            provider: providerName(feature.provider),
          })
        }}
      </p>
    </div>
    <div class="flex flex-wrap items-start flex-shrink-0 gap-3">
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CAPTAIN_SETTINGS.MODEL_CONFIG.MODEL_LABEL') }}
        </span>
        <ModelDropdown
          :feature-key="featureKey"
          @change="emit('change', $event)"
        />
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CAPTAIN_SETTINGS.MODEL_CONFIG.LLM_LABEL') }}
        </span>
        <CredentialDropdown
          :feature-key="featureKey"
          @change="emit('credentialChange', $event)"
        />
      </div>
    </div>
  </div>
</template>
